import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth, useCurrentUserId, useIsAdmin } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon, UserIcon } from '@heroicons/react/24/outline';

const DriverForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, put, loading } = useApi();
  const { user: currentUser } = useAuth();
  const currentUserId = useCurrentUserId();
  const isAdmin = useIsAdmin();
  
  const [formData, setFormData] = useState({
    userId: '',
    otherId: '',
    fullName: '',
    birthDate: '',
    address: '',
    drvState: '',
    country: '',
    postalCode: '',
    mobileNumber: '',
    other: '',
    startedAt: '',
    endedAt: '',
    isActive: true
  });
  
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errors, setErrors] = useState({});

																 

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    } else {
      // Non-admin: auto-assign current user
      setFormData(prev => ({
        ...prev,
        userId: currentUserId || ''
      }));
    }
    
    if (id) {
      fetchDriver();
    }
  }, [id, isAdmin, currentUserId]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await get('/api/users');
      if (data) {
											   
        setUsers(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchDriver = async () => {
    const { data } = await get(`/api/drivers/${id}`);
    if (data) {
      setFormData({
        userId: data.userId || '',
        otherId: data.otherId || '',
        fullName: data.fullName || '',
        birthDate: data.birthDate ? data.birthDate.split('T')[0] : '',
        address: data.address || '',
        drvState: data.drvState || '',
        country: data.country || '',
        postalCode: data.postalCode || '',
        mobileNumber: data.mobileNumber || '',
        other: data.other || '',
        startedAt: data.startedAt ? data.startedAt.slice(0, 16) : '',
        endedAt: data.endedAt ? data.endedAt.slice(0, 16) : '',
        isActive: data.isActive !== undefined ? data.isActive : true
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.userId) {
      newErrors.userId = 'User is required';
    }
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      userId: parseInt(formData.userId),
      otherId: formData.otherId,
      fullName: formData.fullName,
      birthDate: formData.birthDate || null,
      address: formData.address,
      drvState: formData.drvState,
      country: formData.country,
      postalCode: formData.postalCode,
      mobileNumber: formData.mobileNumber,
      other: formData.other,
      startedAt: formData.startedAt || null,
      endedAt: formData.endedAt || null,
      isActive: formData.isActive
    };

    let response;
    if (id) {
      response = await put(`/api/drivers/${id}`, submitData);
    } else {
      response = await post('/api/drivers', submitData);
    }

    if (!response.error) {
      navigate('/drivers/filter');
    }
  };

  if ((loading && id) || loadingUsers) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/drivers/filter')}
          className="mr-4 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          {id ? 'Edit Driver' : 'Add New Driver'}
        </h1>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* User Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                User <span className="text-red-500">*</span>
              </label>
              {isAdmin ? (
                <select
                  name="userId"
                  value={formData.userId}
                  onChange={handleChange}
                  className={`input-field ${errors.userId ? 'border-red-500' : ''}`}
										   
                >
                  <option value="">Select a user</option>
                  {users.map(user => (
                    <option key={user.userId} value={user.userId}>
                      {user.username} - {user.fullName} ({user.email})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={currentUser?.username || 'Current User'}
                  disabled
                  className="input-field bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                />
              )}
              {errors.userId && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.userId}</p>
              )}
							
														  
																										  
					
				
            </div>

            {/* Other ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Other ID
              </label>
              <input
                type="text"
                name="otherId"
                value={formData.otherId}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter other ID"
              />
            </div>

            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className={`input-field ${errors.fullName ? 'border-red-500' : ''}`}
                placeholder="Enter full name"
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.fullName}</p>
              )}
            </div>

            {/* Mobile Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                className={`input-field ${errors.mobileNumber ? 'border-red-500' : ''}`}
                placeholder="Enter mobile number"
              />
              {errors.mobileNumber && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.mobileNumber}</p>
              )}
            </div>

            {/* Birth Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Birth Date
              </label>
              <input
                type="date"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter address"
              />
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                State
              </label>
              <input
                type="text"
                name="drvState"
                value={formData.drvState}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter state"
              />
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter country"
              />
            </div>

            {/* Postal Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Postal Code
              </label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter postal code"
              />
            </div>

            {/* Other */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Other
              </label>
              <input
                type="text"
                name="other"
                value={formData.other}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter other information"
              />
            </div>

            {/* Started At */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Started At
              </label>
              <input
                type="datetime-local"
                name="startedAt"
                value={formData.startedAt}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            {/* Ended At */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ended At
              </label>
              <input
                type="datetime-local"
                name="endedAt"
                value={formData.endedAt}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            {/* Active Status */}
            <div className="md:col-span-2">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 rounded"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</span>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => navigate('/drivers/filter')}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : id ? 'Update Driver' : 'Create Driver'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DriverForm;